import type { Keyword } from '@/types/Keyword';
import styles from '@/components/atoms/ChipMultiSelect/ChipMultiSelect.module.css';
import useSearchSkills from '@/hooks/api-hooks/useSearchSkills';
import { useDebounce } from '@/hooks/useDebounce';
import { Combobox, Transition } from '@headlessui/react';
import { Fragment, useCallback, useState } from 'react';

type ChipMultiSelectProps = {
  value: Keyword[];
  onChange?: (selectedSkills: Keyword[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

export default function SkillDropdown({
  value,
  onChange,
  placeholder,
  disabled,
}: ChipMultiSelectProps) {
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, 50);
  const skillsQuery = useSearchSkills(debouncedQuery);

  const _removeTag = useCallback(
    (personId: Keyword) => {
      const newSelectedPeople = value.filter((person) => person !== personId);
      onChange?.(newSelectedPeople);
    },
    [value, onChange]
  );

  const _onChange = useCallback((selectedItems: Keyword[]) => {
    onChange?.(selectedItems);
    setQuery('');
  }, []);

  return (
    <Combobox value={value} multiple={true} onChange={_onChange} disabled={disabled}>
      <div className={`${styles.dropdownContainer} ${disabled ? styles.disabled : ''}`}>
        <div className={styles.inputContainer}>
          {value.map((person) => (
            <div className={styles.chipContainer} key={person.id}>
              <span className={styles.chipText}>{person.name}</span>
              <button
                type={'button'}
                className={styles.chipCloseButton}
                disabled={disabled}
                onClick={() => _removeTag(person)}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={styles.chipCloseIcon}
                >
                  <path
                    d="M7.5 2.5L2.5 7.5M2.5 2.5L7.5 7.5"
                    stroke="#98A2B3"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ))}
          <Combobox.Input
            className={styles.input}
            placeholder={placeholder}
            value={query}
            aria-disabled={disabled}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Combobox.Button className={styles.dropdownButton}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#667085"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options className={styles.dropdown}>
            {skillsQuery.data?.length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                Nothing found.
              </div>
            ) : (
              skillsQuery.data?.map((skill) => (
                <Combobox.Option
                  key={skill.id}
                  className={({ active }) =>
                    `${styles.comboBoxOption} ${active ? styles.comboBoxOptionActive : ''}`
                  }
                  value={skill}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                      >
                        {skill.name}
                      </span>
                      {selected ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.6668 5L7.50016 14.1667L3.3335 10"
                            stroke="#9B338D"
                            strokeWidth="1.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}
